if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.bisTrigger = this.querySelector('.klaviyo-bis-trigger');
        this.bisTrigger?.addEventListener('click', (event) => {
          event.preventDefault();
        });
        this.onOptionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onOptionValueChange.bind(this)
        );
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
        this.initAtcScrollTrigger();

        if (this.backInStockEnabled && this.bisTrigger && !this.bisTrigger.hasAttribute('hidden')) {
          this.refreshKlaviyoBackInStock();
        }
      }

      disconnectedCallback() {
        this.onOptionChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber?.();
        this.destroyAtcScrollTrigger();
      }

      initAtcScrollTrigger() {
        this.atcWrapper = this.closest('[data-atc-wrapper]');
        if (!this.atcWrapper || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

        gsap.registerPlugin(ScrollTrigger);

        this.atcScrollTriggerMatchMedia = ScrollTrigger.matchMedia({
          '(max-width: 800px)': () => {
            const mainContent = document.getElementById('MainContent');
            if (!mainContent) return;

            const atcWrapper = this.atcWrapper;
            const hideClass = 'product__buy-buttons-wrapper--hide';

            const trigger = ScrollTrigger.create({
              trigger: mainContent,
              start: 'bottom bottom+=10%',
              onEnter: () => atcWrapper.classList.add(hideClass),
              onLeaveBack: () => atcWrapper.classList.remove(hideClass),
              onRefresh: (self) => {
                atcWrapper.classList.toggle(hideClass, self.scroll() >= self.start);
              },
            });

            return () => trigger.kill();
          },
        });
      }

      destroyAtcScrollTrigger() {
        this.atcScrollTriggerMatchMedia?.kill();
        this.atcScrollTriggerMatchMedia = null;
      }

      get backInStockEnabled() {
        return this.dataset.backInStockEnabled === 'true';
      }

      onOptionValueChange({ data: { event } }) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !productInfo.contains(event.target)) return;

        this.updateSubmitButtonLabel();

        if (this.backInStockEnabled && this.bisTrigger && this.hasVariants() && !this.isVariantSelectionComplete()) {
          this.setBackInStockTriggerVisible(false);
        }
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (!this.backInStockEnabled || sectionId !== this.dataset.sectionId) return;
        this.setBackInStockTriggerVisible(this.shouldShowBackInStockTrigger(variant));
      }

      shouldShowBackInStockTrigger(variant) {
        if (!variant) return false;
        if (variant.available) return false;
        if (variant.inventory_management !== 'shopify') return false;
        if (variant.inventory_policy === 'continue') return false;
        return true;
      }

      setBackInStockTriggerVisible(show) {
        if (!this.backInStockEnabled || !this.bisTrigger) return;

        this.bisTrigger.toggleAttribute('hidden', !show);
        this.bisTrigger.classList.toggle('hidden', !show);
        this.setSubmitButtonVisible(!show);

        if (show) {
          this.refreshKlaviyoBackInStock();
        }
      }

      setSubmitButtonVisible(show) {
        if (!this.submitButton) return;

        this.submitButton.toggleAttribute('hidden', !show);
        this.submitButton.classList.toggle('hidden', !show);
      }

      refreshKlaviyoBackInStock() {
        window.setTimeout(() => {
          if (window._klaviyo?.init) {
            window._klaviyo.init();
          }

          document.dispatchEvent(new CustomEvent('variant:change'));
        }, 150);
      }

      hasVariants() {
        return !!this.closest('product-info')?.querySelector('variant-selects');
      }

      isVariantSelectionComplete() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return true;

        return Array.from(variantSelects.querySelectorAll('fieldset')).every((fieldset) => {
          return fieldset.querySelector('input[type="radio"]:checked');
        });
      }

      missingOptionsMessage() {
        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        const selectOption = window.variantStrings?.selectOption ?? 'Please Select [options]';
        if (!variantSelects) return selectOption.replace('[options]', '');

        const missing = Array.from(variantSelects.querySelectorAll('fieldset'))
          .filter((fieldset) => !fieldset.querySelector('input[type="radio"]:checked'))
          .map((fieldset) => fieldset.querySelector('legend')?.textContent.trim().replace(/:$/, ''))
          .filter(Boolean);

        return selectOption.replace('[options]', missing.join(', '));
      }

      updateSubmitButtonLabel() {
        if (!this.hasVariants() || !this.submitButtonText) return;

        if (this.isVariantSelectionComplete()) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.hasVariants() && !this.isVariantSelectionComplete()) {
          this.submitButtonText.textContent = this.missingOptionsMessage();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        this.setBackInStockTriggerVisible(
          this.backInStockEnabled &&
            disable &&
            text === window.variantStrings.soldOut &&
            this.shouldShowBackInStockTriggerFromButton()
        );
      }

      shouldShowBackInStockTriggerFromButton() {
        const variantId = this.variantIdInput?.value;
        if (!variantId) return false;

        const variantData = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (!variantData) return this.submitButton.hasAttribute('disabled');

        try {
          return this.shouldShowBackInStockTrigger(JSON.parse(variantData));
        } catch {
          return this.submitButton.hasAttribute('disabled');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
